import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AppThunk, createAppAsyncThunk } from "appThunk";
import { logError } from "modules/helpers/logger/loggerSlice";
import { RootState } from "store";

import { AverageDailyFootfall, loadAverageDailyFootfall } from "./averageDailyFootfall";

import { selectComparatorsByChapter, selectPinnedLocation } from "modules/customer/tools/location/locationSlice";
import { FootfallOverTime, loadFootfallOverTime } from "./footfallOverTime";
import { DateTime } from "luxon";
import _, { maxBy } from "lodash";
import { max, median } from "mathjs";
import { FootfallGranularity } from "./footfallGranularity";
import { FootfallMeasureType } from "./footfallMeasureType";
import { FootfallByArea, loadFootfallByArea } from "./footfallByArea";
import mathUtils from "utils/mathUtils";

interface LoadFootfallResponse {
    averageDailyFootfall: AverageDailyFootfall[],
    footfallByArea: FootfallByArea[],
    footfallOverTime: FootfallOverTime[],
}

interface FootfallState {
    isLoading: boolean,
    hasErrors: boolean,
    averageDailyFootfall: AverageDailyFootfall[],
    footfallByArea: FootfallByArea[],
    footfallByAreaMeasure: FootfallMeasureType,
    footfallOverTime: FootfallOverTime[],
    footfallOverTimeGranularity: FootfallGranularity,
    footfallOverTimeMeasure: FootfallMeasureType,
    averageDailyFootfallMeasure: FootfallMeasureType
}

const initialState: FootfallState = {
    isLoading: false,
    hasErrors: false,
    averageDailyFootfall: [],
    footfallByArea: [],
    footfallByAreaMeasure: FootfallMeasureType.NumberOfVisits,
    footfallOverTime: [],
    footfallOverTimeGranularity: FootfallGranularity.Month,
    footfallOverTimeMeasure: FootfallMeasureType.NumberOfVisits,
    averageDailyFootfallMeasure: FootfallMeasureType.NumberOfVisits
};

const footfallSlice = createSlice({
    name: "customer/tools/location/footfallNew",
    initialState,
    reducers: {
        clearAverageDailyFootfall: (state) => {
            state.averageDailyFootfall = [];
            state.averageDailyFootfallMeasure = initialState.averageDailyFootfallMeasure;
        },
        clearFootfallByArea: (state) => {
            state.footfallByArea = [];
            state.footfallByAreaMeasure = initialState.footfallByAreaMeasure;
        },
        clearFootfallOverTime: (state) => {
            state.footfallOverTime = initialState.footfallOverTime;
            state.footfallOverTimeGranularity = initialState.footfallOverTimeGranularity;
            state.footfallOverTimeMeasure = initialState.footfallOverTimeMeasure;
        },
        setFootfallByAreaMeasure: (state, action: PayloadAction<FootfallMeasureType>) => {
            state.footfallByAreaMeasure = action.payload;
        },
        setFootfallOverTimeGranularity: (state, action: PayloadAction<FootfallGranularity>) => {
            state.footfallOverTimeGranularity = action.payload;
        },
        setFootfallOverTimeMeasure: (state, action: PayloadAction<FootfallMeasureType>) => {
            state.footfallOverTimeMeasure = action.payload;
        },
        setAverageDailyFootfallMeasure: (state, action: PayloadAction<FootfallMeasureType>) => {
            state.averageDailyFootfallMeasure = action.payload;
        },
    },
    extraReducers: (builder: any) => {
        builder.addCase(loadFootfall.pending, (state: FootfallState) => {
            state.isLoading = true;
            state.hasErrors = false;
            state.footfallOverTime = initialState.footfallOverTime;
            state.averageDailyFootfall = initialState.averageDailyFootfall;
            state.footfallByArea = initialState.footfallByArea;
        });
        builder.addCase(loadFootfall.rejected, (state: FootfallState) => {
            state.isLoading = false;
            state.hasErrors = true;
            state.footfallOverTime = initialState.footfallOverTime;
            state.averageDailyFootfall = initialState.averageDailyFootfall;
            state.footfallByArea = initialState.footfallByArea;
        });
        builder.addCase(loadFootfall.fulfilled, (state: FootfallState, action: PayloadAction<LoadFootfallResponse>) => {
            state.isLoading = false;
            state.hasErrors = false;
            state.footfallOverTime = action.payload.footfallOverTime;
            state.averageDailyFootfall = action.payload.averageDailyFootfall;
            state.footfallByArea = action.payload.footfallByArea;
        });
    }
});

export const {
    setFootfallByAreaMeasure,
    setFootfallOverTimeGranularity,
    setFootfallOverTimeMeasure,
    setAverageDailyFootfallMeasure
} = footfallSlice.actions;

export const loadFootfall = createAppAsyncThunk(
    "customer/tools/location/footfallNew/loadFootfall",
    async (arg, thunkAPI) => {
        try {
            const state = thunkAPI.getState();
            const pinnedLocation = selectPinnedLocation(state);
            const pinnedLocationRetailCentreId = pinnedLocation?.retailCentre.id;
            const comparatorRetailCentreId = selectComparatorsByChapter(state)?.footfall?.retailCentreId;

            const retailCentreIds = [];
            if (pinnedLocationRetailCentreId) {
                retailCentreIds.push(pinnedLocationRetailCentreId);
            }
            if (comparatorRetailCentreId) {
                retailCentreIds.push(comparatorRetailCentreId);
            }
            const footfallOverTimePromise = thunkAPI.dispatch(loadFootfallOverTime(retailCentreIds));
            const averageDailyFootfallPromise = thunkAPI.dispatch(loadAverageDailyFootfall(pinnedLocationRetailCentreId));
            const footfallByAreaPromise = thunkAPI.dispatch(loadFootfallByArea(pinnedLocation));

            const result = await Promise.all([
                averageDailyFootfallPromise,
                footfallOverTimePromise,
                footfallByAreaPromise,
            ]);

            const loadFootfallResponse: LoadFootfallResponse = {
                averageDailyFootfall: result[0],
                footfallOverTime: result[1],
                footfallByArea: result[2],
            };
            return loadFootfallResponse;
        } catch (error) {
            thunkAPI.dispatch(logError("Error loading FootfallNew.", error));
            return thunkAPI.rejectWithValue(null);
        }
    }
);

export const clearFootfall = (): AppThunk => (dispatch) => {
    dispatch(footfallSlice.actions.clearAverageDailyFootfall());
    dispatch(footfallSlice.actions.clearFootfallByArea());
    dispatch(footfallSlice.actions.clearFootfallOverTime());
};

export const selectIsLoading = (state: RootState) => {
    return state.customer.tools.location.footfallNew.isLoading;
};

export const selectHasErrors = (state: RootState) => {
    return state.customer.tools.location.footfallNew.hasErrors;
};

export const selectAverageDailyFootfall = (state: RootState) => {
    return state.customer.tools.location.footfallNew.averageDailyFootfall;
};

export const selectFootfallByArea = (state: RootState) => {
    return state.customer.tools.location.footfallNew.footfallByArea;
};

export const selectFootfallByAreaMeasure = (state: RootState) => {
    return state.customer.tools.location.footfallNew.footfallByAreaMeasure;
};

export const selectFootfallOverTime = (state: RootState) => {
    return state.customer.tools.location.footfallNew.footfallOverTime;
};

export const selectFootfallOverTimeGranularity = (state: RootState) => {
    return state.customer.tools.location.footfallNew.footfallOverTimeGranularity;
};

export const selectFootfallOverTimeMeasure = (state: RootState) => {
    return state.customer.tools.location.footfallNew.footfallOverTimeMeasure;
};

export const selectAverageDailyFootfallMeasure = (state: RootState) => {
    return state.customer.tools.location.footfallNew.averageDailyFootfallMeasure;
};

export const selectLastTwelveMonthsTotalVisits = createSelector(
    (state: RootState) => selectPinnedLocation(state),
    (state: RootState) => selectComparatorsByChapter(state),
    selectFootfallOverTime,
    (pinnedLocation, comparators, footfallOverTime) => {
        const totalVisits = {
            selectedStore: 0,
            comparator: 0,
            percentageDifference: 0
        };
        if (!pinnedLocation || !comparators?.footfall || footfallOverTime.length === 0) {
            return totalVisits;
        }

        const mostRecentItem = maxBy(footfallOverTime, item => item.date.toMillis());
        const twelveMonthsAgoDate = mostRecentItem?.date.minus({ years: 1 });
        const previousTwelveMonthsFootfall = twelveMonthsAgoDate ? footfallOverTime.filter(item => item.date > twelveMonthsAgoDate) : [];

        const selectedStoreFootfall = previousTwelveMonthsFootfall.filter(item => item.retailCentreId === pinnedLocation.retailCentre.id);
        totalVisits.selectedStore = selectedStoreFootfall.reduce((acc, item) => acc + item.numberOfVisits, 0);

        const comparatorFootfall = previousTwelveMonthsFootfall.filter(item => item.retailCentreId === comparators.footfall.retailCentreId);

        const comparatorValues = _(comparatorFootfall)
            .groupBy(item => item.retailCentreId)
            .map((group, retailCentreId) => ({
                retailCentreId,
                numberOfVisits: group.reduce((acc, item) => acc + item.numberOfVisits, 0)
            }))
            .value();

        totalVisits.comparator = comparatorValues.length === 0
            ? 0
            : median(comparatorValues.map(item => item.numberOfVisits));

        totalVisits.percentageDifference = mathUtils.safePercentageChange(totalVisits.selectedStore, totalVisits.comparator);

        return totalVisits;
    }
);

export const selectLatestMonthFootfall = createSelector(
    (state: RootState) => selectPinnedLocation(state),
    (state: RootState) => selectComparatorsByChapter(state),
    selectFootfallOverTime,
    (pinnedLocation, comparators, footfallOverTime) => {
        if (!pinnedLocation || !comparators?.footfall || footfallOverTime.length === 0) {
            return {
                latestMonthDate: DateTime.fromMillis(0),
                numberOfVisits: { selectedStore: 0, comparator: 0, percentageDifference: 0 },
                dwellTime: { selectedStore: 0, comparator: 0, percentageDifference: 0 }
            };
        }

        const mostRecentMillis = max(footfallOverTime.map(item => item.date.toMillis()));
        const mostRecentDate = DateTime.fromMillis(mostRecentMillis, { zone: "utc" });
        const latestMonthFootfallValues = footfallOverTime.filter(item => item.date.equals(mostRecentDate));

        const selectedStoreFootfall = latestMonthFootfallValues.find(item =>
            item.retailCentreId === pinnedLocation.retailCentre.id
        ) ?? { numberOfVisits: 0, dwellTime: 0 };

        const comparatorValues = latestMonthFootfallValues.filter(item => item.retailCentreId === comparators.footfall.retailCentreId);

        const comparatorMedian = {
            numberOfVisits: comparatorValues.length === 0 ? 0 : median(comparatorValues.map(item => item.numberOfVisits)),
            dwellTime: comparatorValues.length === 0 ? 0 : median(comparatorValues.map(item => item.dwellTime))
        };

        return {
            latestMonthDate: mostRecentDate,
            numberOfVisits: {
                selectedStore: selectedStoreFootfall.numberOfVisits,
                comparator: comparatorMedian.numberOfVisits,
                percentageDifference: mathUtils.safePercentageChange(selectedStoreFootfall.numberOfVisits, comparatorMedian.numberOfVisits)
            },
            dwellTime: {
                selectedStore: selectedStoreFootfall.dwellTime,
                comparator: comparatorMedian.dwellTime,
                percentageDifference: mathUtils.safePercentageChange(selectedStoreFootfall.dwellTime, comparatorMedian.dwellTime)
            }
        };
    }
);

interface RetailCentreFootfallOverTime {
    selectedLocation: FootfallByDate[],
    comparator: FootfallByDate[]
}

interface FootfallByDate {
    date: DateTime,
    numberOfVisits: number,
    dwellTime: number
}

export const selectRetailCentreFootfallOverTime = createSelector(
    (state: RootState) => selectPinnedLocation(state),
    (state: RootState) => selectComparatorsByChapter(state),
    selectFootfallOverTime,
    (pinnedLocation, comparators, footfallOverTime) => {
        const retailCentreFootfallOverTime: RetailCentreFootfallOverTime = {
            selectedLocation: [],
            comparator: []
        };
        if (!pinnedLocation || !comparators?.footfall || footfallOverTime.length === 0) {
            return retailCentreFootfallOverTime;
        }

        const selectedLocationFootfall = footfallOverTime.filter(item => item.retailCentreId === pinnedLocation.retailCentre.id);
        retailCentreFootfallOverTime.selectedLocation = selectedLocationFootfall.map(item => ({
            date: item.date,
            numberOfVisits: item.numberOfVisits,
            dwellTime: item.dwellTime
        }));

        const comparatorFootfall = footfallOverTime.filter(item => item.retailCentreId === comparators.footfall.retailCentreId);

        retailCentreFootfallOverTime.comparator = _(comparatorFootfall)
            .groupBy(item => item.date.toMillis())
            .map((group, key) => ({
                date: DateTime.fromMillis(Number(key), { zone: 'utc' }),
                numberOfVisits: (group.length !== 0) ? median(group.map(item => item.numberOfVisits)) : 0,
                dwellTime: (group.length !== 0) ? median(group.map(item => item.dwellTime)) : 0
            }))
            .value();

        return retailCentreFootfallOverTime;
    }
);

export const selectFootfallCentiles = createSelector(
    (state: RootState) => selectPinnedLocation(state),
    (state: RootState) => selectComparatorsByChapter(state),
    (pinnedLocation, comparators) => {
        const footfallCentiles = {
            selectedLocation: 0,
            comparator: 0
        };
        if (!pinnedLocation || !comparators?.footfall) {
            return footfallCentiles;
        }

        footfallCentiles.selectedLocation = pinnedLocation.retailCentre.footfallCentile;
        footfallCentiles.comparator = comparators.footfall.store.retailCentre.footfallCentile;
        return footfallCentiles;
    }
);

const FOOTFALL_MAP_CENTILES = [0, 0.4, 0.8, 0.9, 0.95];

export const selectFootfallByAreaMapThresholds = createSelector(
    selectIsLoading,
    selectHasErrors,
    selectFootfallByArea,
    (isLoading, hasErrors, footfallByArea) => {
        if (isLoading || hasErrors || footfallByArea.length === 0) {
            return { visitsThresholds: [], dwellThresholds: [] };
        }

        const visitsValues = footfallByArea.map(item => item.numberOfVisits);
        const dwellValues = footfallByArea.map(item => item.dwellTime);

        const visitsThresholds = mathUtils.customPercentileThresholds(visitsValues, FOOTFALL_MAP_CENTILES);
        const dwellThresholds = mathUtils.customPercentileThresholds(dwellValues, FOOTFALL_MAP_CENTILES);

        return { visitsThresholds, dwellThresholds };
    }
);

export interface FootfallByAreaWithColourGroups extends FootfallByArea {
    visitsColourGroup: number,
    dwellColourGroup: number
}

export const selectFootfallByAreaWithColourGroups = createSelector(
    selectIsLoading,
    selectHasErrors,
    selectFootfallByArea,
    selectFootfallByAreaMapThresholds,
    (isLoading, hasErrors, footfallByArea, thresholds) => {
        if (isLoading || hasErrors || footfallByArea.length === 0) {
            return [];
        }

        const { visitsThresholds, dwellThresholds } = thresholds;
        const footfallByAreaWithCentiles: FootfallByAreaWithColourGroups[] = footfallByArea.map(item => {
            let visitsColourGroup = 0;
            for (let i = 0; i < visitsThresholds.length; i++) {
                if (item.numberOfVisits >= visitsThresholds[i]) {
                    visitsColourGroup = (visitsThresholds.length - 1) - i;
                }
            }
            let dwellColourGroup = 0;
            for (let i = 0; i < dwellThresholds.length; i++) {
                if (item.dwellTime >= dwellThresholds[i]) {
                    dwellColourGroup = (dwellThresholds.length - 1) - i;
                }
            }
            return {
                ...item,
                visitsColourGroup,
                dwellColourGroup
            };
        });
        return footfallByAreaWithCentiles;
    }
);

export interface AverageFootfallHeatMapData {
    footfallData: AverageDailyFootfall[],
    maxFootfallCount: number,
    maxDwellTime: number
}

export const selectAverageFootfallHeatMapData = createSelector(
    selectIsLoading,
    selectHasErrors,
    selectAverageDailyFootfall,
    (isLoading, hasErrors, averageFootfall) => {
        const averageDailyFootfallData: AverageFootfallHeatMapData = {
            footfallData: [],
            maxFootfallCount: 0,
            maxDwellTime: 0
        };

        if (isLoading || hasErrors || averageFootfall.length === 0) {
            return averageDailyFootfallData;
        }

        const mostRecentMillis = max(averageFootfall.map(item => item.monthDate.toMillis()));
        const mostRecentDate = DateTime.fromMillis(mostRecentMillis, { zone: "utc" });
        const latestMonthFootfall = averageFootfall.filter(item => item.monthDate.equals(mostRecentDate));

        const sortedByFootfallCount = latestMonthFootfall.map(x => x.footfallCount).sort((a, b) => b - a);
        const maxFootfallCount = sortedByFootfallCount[0];

        const sortedByDwellTime = latestMonthFootfall.map(x => x.dwellTime).sort((a, b) => b - a);
        const maxDwellTime = sortedByDwellTime[0];

        averageDailyFootfallData.footfallData = latestMonthFootfall;
        averageDailyFootfallData.maxFootfallCount = maxFootfallCount;
        averageDailyFootfallData.maxDwellTime = maxDwellTime;

        return averageDailyFootfallData;
    }
);

export default footfallSlice;
